import React, { useEffect, useState } from 'react';
import { MarshmallowSkewer } from './MarshmallowSkewer';

interface MarshmallowGaugeProps {
  remaining: number;
  total: number;
  burned: number;
  onBurnAnimationEnd?: () => void;
}

export const MarshmallowGauge: React.FC<MarshmallowGaugeProps> = ({
  remaining,
  total,
  burned,
  onBurnAnimationEnd,
}) => {
  const [showBurn, setShowBurn] = useState(false);

  const percentage = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color based on percentage
  const getGaugeColor = () => {
    if (percentage > 50) return '#22c55e'; // green
    if (percentage > 25) return '#eab308'; // yellow
    if (percentage > 0) return '#ef4444'; // red
    return '#374151'; // dark gray (depleted)
  };

  const getGlowColor = () => {
    if (percentage > 50) return 'rgba(34, 197, 94, 0.3)';
    if (percentage > 25) return 'rgba(234, 179, 8, 0.3)';
    if (percentage > 0) return 'rgba(239, 68, 68, 0.3)';
    return 'rgba(55, 65, 81, 0.2)';
  };

  // Trigger burn animation when burned > 0
  useEffect(() => {
    if (burned > 0) {
      setShowBurn(true);
      const timer = setTimeout(() => {
        setShowBurn(false);
        onBurnAnimationEnd?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [burned, onBurnAnimationEnd]);

  return (
    <div className="relative flex flex-col items-center">
      {/* SVG Gauge */}
      <div className="relative w-36 h-36">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          {/* Background ring */}
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#1e293b" strokeWidth="8" />
          {/* Progress ring */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={getGaugeColor()}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
            style={{
              filter: `drop-shadow(0 0 6px ${getGlowColor()})`,
            }}
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {percentage <= 0 ? (
            <img
              src="/roary/roary-worried.png"
              alt="Out of marshmallows"
              className="w-14 h-14 object-contain"
            />
          ) : (
            <MarshmallowSkewer className="w-12 h-12" />
          )}
          <span className="text-white font-bold text-lg leading-tight">{remaining}</span>
          <span className="text-gray-500 text-xs">/ {total}</span>
        </div>

        {/* Low warning pulse */}
        {percentage > 0 && percentage <= 25 && (
          <div
            className="absolute inset-0 rounded-full animate-pulse"
            style={{
              boxShadow: `0 0 20px ${getGlowColor()}`,
            }}
          />
        )}
      </div>

      {/* Burn animation */}
      {showBurn && burned > 0 && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: Math.min(burned, 8) }).map((_, i) => (
            <div
              key={i}
              className="absolute animate-marshmallow-burn"
              style={{
                left: `${30 + Math.random() * 40}%`,
                bottom: '30%',
                animationDelay: `${i * 0.15}s`,
              }}
            >
              <MarshmallowSkewer className="w-5 h-5" />
            </div>
          ))}
        </div>
      )}

      {/* Burn count label */}
      {showBurn && burned > 0 && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 animate-fade-up">
          <span className="inline-flex items-center gap-1 text-red-400 text-sm font-semibold">
            -{burned}
            <MarshmallowSkewer className="w-3.5 h-3.5" />
          </span>
        </div>
      )}
    </div>
  );
};
